import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Lock, Save } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, Chip, PageHead, Section, TONES, Toggle } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";
import { themeOf } from "@/lib/mall";

const TONE = TONES.marketing;

/**
 * A thumbnail of your window on the 13thav homepage with one block lit up, so you can see
 * exactly which piece of the page a switch controls before you flip it.
 */
function Wire({ lit, color }) {
    const block = (key, cls) => (
        <span className={`block rounded-[2px] ${cls} ${lit === key ? "" : "bg-zinc-200"}`} style={lit === key ? { background: color } : undefined} />
    );
    return (
        <div className="w-[120px] shrink-0 rounded-md border border-zinc-300 bg-white p-1.5" aria-hidden data-testid={`wire-${lit}`}>
            <span className="mb-1 block h-1.5 w-8 rounded-[2px] bg-zinc-300" />
            {block("directory", "mb-1 h-3 w-full")}
            {block("billboard", "mb-1 h-6 w-full")}
            <span className="mb-1 grid grid-cols-3 gap-1">
                {[0, 1, 2].map((i) => <span key={i} className={`block h-4 rounded-[2px] ${lit === "products" ? "" : "bg-zinc-200"}`} style={lit === "products" ? { background: color } : undefined} />)}
            </span>
            <span className="grid grid-cols-2 gap-1">
                {block("website", "h-4")}
                {block("artwork", "h-4")}
            </span>
            {lit === "builds" && <span className="mt-1 block h-2 w-1/2 rounded-[2px]" style={{ background: color }} />}
        </div>
    );
}

/** Every block of your window, in the order it appears on the page. */
const BLOCKS = [
    {
        key: "directory", field: "featured_home", label: "Listed in the directory",
        what: "Your shop card in the 13thav directory list and your pin on the map.",
        where: "13thav homepage · the shop list and the map", always: true,
    },
    {
        key: "billboard", slot: "billboard", label: "Billboard",
        what: "Your newest billboard hangs at the top of your window on the 13thav homepage.",
        where: "13thav homepage · your window, top", needs: "Pro Shop or Virtual Brick & Mortar",
    },
    {
        key: "products", slot: "products", label: "Three products",
        what: "Three things from your shelves, with prices, in your window.",
        where: "13thav homepage · your window, middle", needs: "Storefront or better",
    },
    {
        key: "website", slot: "website", label: "Website window",
        what: "Your website-design pitch beside your window, for shoppers who need a site.",
        where: "13thav homepage · beside your window", needs: "Pro Shop or Virtual Brick & Mortar",
    },
    {
        key: "artwork", slot: "artwork", label: "Artwork window",
        what: "Your artwork department beside your window, for shoppers who need a file made.",
        where: "13thav homepage · beside your window", needs: "Pro Shop or Virtual Brick & Mortar",
    },
    {
        key: "builds", field: "builds_websites", label: "We build websites", tier: "department",
        what: 'Puts your shop on the 13thav-wide "Need a website?" picker. Requests email straight to you.',
        where: "Every page · the website picker", needs: "Pro Shop",
    },
];

/** What a shop shows on the 13thav homepage: one block per line, a picture of where it sits, one switch. */
export default function StoreHomepage() {
    const [me, setMe] = useState(null);
    const [flags, setFlags] = useState({ featured_home: true, builds_websites: false });
    const [slots, setSlots] = useState({ billboard: true, products: true, website: true, artwork: true });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/stores/me").then(({ data }) => {
            setMe(data);
            if (!data.store) return;
            setFlags({ featured_home: data.store.featured_home !== false, builds_websites: !!data.store.builds_websites });
            setSlots((s) => ({ ...s, ...(data.slots || {}) }));
        }).catch(() => setMe({}));
    }, []);

    const save = async () => {
        const s = me.store;
        setBusy(true);
        try {
            const theme = themeOf(s);
            await api.post("/stores/apply", {
                ...s, tier: me?.tier?.id || s.tier || "kiosk",
                address: s.address || s.pickup_address || "",
                pickup_enabled: s.pickup_enabled ?? Boolean(s.pickup_address),
                pickup_hours: s.pickup_hours || s.hours || "",
                shipping_mode: s.shipping_mode || (s.ships ? "ships_5" : ""),
                crew_perks_honored: !!s.crew_perks_honored,
                theme_primary: theme.primary, theme_secondary: theme.secondary, theme_tertiary: theme.tertiary,
                featured_kind: s.featured?.kind || null, featured_slug: s.featured?.slug || null, featured_headline: s.featured?.headline || "",
                lines: s.lines && Object.keys(s.lines).length ? s.lines : null,
                featured_home: !!flags.featured_home, builds_websites: !!flags.builds_websites,
                slot_billboard: slots.billboard, slot_products: slots.products, slot_website: slots.website, slot_artwork: slots.artwork,
            });
            toast.success("Homepage window saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!me) return <BackOffice><div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-zinc-400" /></div></BackOffice>;
    if (!me.store) {
        return (
            <BackOffice>
                <div className="mx-auto max-w-2xl py-20 text-center" data-testid="homepage-locked">
                    <Lock size={32} className="mx-auto text-zinc-400" />
                    <h1 className="mt-3 font-display text-2xl font-black tracking-tight text-zinc-900">A homepage window comes with a store.</h1>
                    <Link to="/account/sell" className="mt-4 inline-block"><Btn tone="solid" testid="homepage-open-space">See the spaces</Btn></Link>
                </div>
            </BackOffice>
        );
    }

    const caps = me.slot_caps || {};
    const color = themeOf(me.store).primary;
    const tier = me.tier?.id || me.store.tier;
    const on = (b) => (b.slot ? !!slots[b.slot] : !!flags[b.field]);
    const can = (b) => (b.slot ? !!caps[b.slot] : b.tier ? tier === b.tier : true);
    const flip = (b, v) => (b.slot ? setSlots((s) => ({ ...s, [b.slot]: v })) : setFlags((f) => ({ ...f, [b.field]: v })));

    return (
        <BackOffice>
            <PageHead kicker="Marketing" tone={TONE} title="Your homepage window" testid="homepage-head"
                sub="Every shop gets a window on the 13thav homepage. Each line below is one block of that window, in the order it appears on the page — the picture shows where it sits, the switch turns it on or off. Your own shop page's hero is under Store profile → Your store billboard."
                right={
                    <Link to="/directory" data-testid="homepage-view" className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-800 hover:border-zinc-900">
                        See the homepage <ArrowUpRight size={12} />
                    </Link>
                } />

            <div className="mt-3 max-w-5xl">
                <Section title={`${BLOCKS.filter((b) => b.tier ? tier === b.tier : true).length} blocks`} tone={TONE} pad={false} testid="homepage-blocks"
                    note="top to bottom, the way shoppers see them">
                    <div className="grid border-b-2 border-[#1a1a1a] bg-[#fafafa] px-2.5 py-1.5 text-[11px] font-bold uppercase text-[#1a1a1a] sm:grid-cols-[136px_minmax(0,1fr)_170px_120px]" data-testid="homepage-header">
                        <span>Where it sits</span><span>Block · what it is</span><span>Needs</span><span className="sm:text-right">On the page?</span>
                    </div>
                    {BLOCKS.filter((b) => (b.tier ? tier === b.tier : true)).map((b) => {
                        const ok = can(b);
                        return (
                            <div key={b.key} className={`grid items-center gap-3 border-t border-[#e5e5e5] px-2.5 py-2.5 sm:grid-cols-[136px_minmax(0,1fr)_170px_120px] ${ok ? "" : "bg-[#fafafa]"}`}
                                data-testid={`homepage-block-${b.key}`}>
                                <Wire lit={b.key} color={ok && on(b) ? color : "#a1a1aa"} />
                                <div className="min-w-0">
                                    <p className="text-[13px] font-semibold text-[#1a1a1a]">{b.label}</p>
                                    <p className="mt-0.5 text-[12px] leading-snug text-[#4a4a4a]">{b.what}</p>
                                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[#767676]">{b.where}</p>
                                </div>
                                <div>
                                    {b.needs
                                        ? <Chip tone={ok ? "green" : "amber"} testid={`homepage-needs-${b.key}`}>{ok ? "included in your plan" : b.needs}</Chip>
                                        : <Chip tone="neutral" testid={`homepage-needs-${b.key}`}>every plan</Chip>}
                                </div>
                                <div className="flex items-center gap-2 sm:justify-end">
                                    {ok
                                        ? <Toggle on={on(b)} onChange={(v) => flip(b, v)} testid={`homepage-toggle-${b.key}`} />
                                        : <Link to="/account/sell" className="text-[11px] font-semibold uppercase text-[#1a56db] underline" data-testid={`homepage-upgrade-${b.key}`}>upgrade</Link>}
                                </div>
                            </div>
                        );
                    })}
                </Section>

                <div className="sticky bottom-0 z-30 -mx-3.5 mt-3 flex flex-wrap items-center gap-2 border-t border-black/10 bg-white/95 px-3.5 py-2 backdrop-blur sm:-mx-5 sm:px-5">
                    <Btn onClick={save} tone="solid" disabled={busy} testid="homepage-save">
                        {busy ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />} Save my window
                    </Btn>
                    <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-700">live on the homepage the moment you save</p>
                </div>
            </div>
        </BackOffice>
    );
}
